
  import { defineComponent, PropType } from 'vue';
  import useVuelidate from '@vuelidate/core';

  import ComponentInput from '@/common/components/ComponentInput.vue';
  import CheckboxInput from '@/common/components/redesigned/CheckboxInput.vue';
  import InputBlock from '@/common/components/redesigned/InputBlock.vue';
  import SelectMultiComponentCheckbox from '@/common/components/redesigned/SelectRedesigned/SelectMultiComponentCheckbox.vue';
  import TreeSelectionProducts from '@/common/components/redesigned/TreeSelectionProducts/TreeSelectionProducts.vue';
  import SelectMultiComponent from '@/common/components/SelectMultiComponent.vue';
  import removeEmptyValuesFromObject from '@/common/mixins/removeEmptyValuesFromObject';
  import security from '@/common/mixins/security';
  import text from '@/common/mixins/text';
  import { deepEqual } from '@/common/utils/objects';

  import DateRangeField from '../RangeFields/DateRangeField.vue';
  import NumberRangeField from '../RangeFields/NumberRangeField.vue';
  import TimeRangeField from '../RangeFields/TimeRangeField.vue';
  import TreeMultiselect from '../TreeMultiselect/TreeMultiselect.vue';
  import FilterActions from './FilterActions.vue';
  import FilterWrapper from './FilterWrapper.vue';
  import type {
    TBaseFilter,
    TBaseFilterDates,
    TBaseFilterRange,
    TBaseFilterSelect,
    TFilter,
    TFilterActionButton,
    TFilterConfig,
    TFilterMultiSelect,
    TFilterSelect,
    TFilterTreeSelect,
  } from './types/filter';
  import { FilterPostfixKey, RangeTypes } from './types/filter';

  export default defineComponent({
    name: 'FilterFactory',
    emits: {
      searchFilter: (value: TFilter) => value,
      updateFilter: (value: TFilter) => value,
      resetFilter: () => true,
    },
    mixins: [removeEmptyValuesFromObject, text, security],
    components: {
      InputBlock,
      DateRangeField,
      NumberRangeField,
      TimeRangeField,
      FilterActions,
      SelectMultiComponent,
      ComponentInput,
      CheckboxInput,
      TreeMultiselect,
      TreeSelectionProducts,
      SelectMultiComponentCheckbox,
      FilterWrapper,
    },
    data(): {
      filterSnapshot: TFilter;
      filterFirstSnapshot: TFilter;
      filter: TFilter;
      filterPostfixKey: typeof FilterPostfixKey;
      isReadonly: boolean;
    } {
      return {
        filterSnapshot: {},
        filterFirstSnapshot: {},
        filter: {},
        filterPostfixKey: FilterPostfixKey,
        isReadonly: true,
      };
    },
    props: {
      filtersConfig: {
        type: Array as PropType<TFilterConfig[]>,
        required: true,
      },
      defaultFilter: {
        type: Object as PropType<TFilter>,
        default: () => ({}),
      },
      isShowResetButton: {
        type: Boolean,
        default: true,
      },
      isShowApplyButton: {
        type: Boolean,
        default: true,
      },
      selectButtonText: {
        type: String,
        default: 'Показать',
      },
      isShowDefaultButton: {
        type: Boolean,
        default: false,
      },
      readonlyPermissionCode: {
        type: Number,
      },
      hasActiveSort: {
        type: Boolean,
        default: false,
      },
    },
    created() {
      this.initFilter();
      this.initFilterSnapshots();
    },
    setup() {
      return { v$: useVuelidate() };
    },
    computed: {
      filterActions(): TFilterActionButton[] {
        return [
          {
            text: this.selectButtonText,
            mod: 'gradient-bg',
            disabled: this.getIsDisabledApplyButton,
            isVisible: this.isShowApplyButton,
            onClick: () => this.searchFilter(),
          },
          {
            text: 'Сбросить',
            disabled: this.getIsDisabledEventButton,
            isVisible: this.isShowResetButton,
            onClick: () => this.resetFilter(),
          },
          {
            text: 'Выбрать по умолчанию',
            mod: 'transparent-bg',
            disabled: this.getIsDisabledDefaultButton,
            isVisible: this.isShowDefaultButton,
            onClick: () => this.initFilter(),
          },
        ];
      },
      getIsFilterEmpty() {
        return !Object.keys(this.getFilter).length;
      },
      getIsFilterSnapshotEmpty() {
        return !Object.keys(this.filterSnapshot).length;
      },
      getIsDisabledEventButton() {
        return this.getIsFilterEmpty && this.getIsFilterSnapshotEmpty && !this.hasActiveSort;
      },
      getIsDisabledApplyButton() {
        return deepEqual(this.getFilter, this.filterSnapshot) || this.v$.$invalid || this.isReadonly;
      },
      getIsDisabledDefaultButton() {
        return deepEqual(this.getFilter, this.filterFirstSnapshot) || this.isReadonly;
      },
      getFilter() {
        const filtersVModelKeys = this.filtersConfig.flatMap(this.getVModelKey);
        return Object.fromEntries(
          Object.entries(this.objectFormatter(this.filter)).filter(
            ([key, value]) => filtersVModelKeys.includes(key) && value !== false,
          ),
        );
      },
    },
    methods: {
      getDefaultFilter(filterConfig: TFilterConfig[], acc: TFilter) {
        const result: TFilter = {};
        filterConfig.forEach((filterConfig) => {
          // ? ключи объекта соответствуют значениям vModel полей
          switch (filterConfig.type) {
            case 'text':
            case 'select':
              result[filterConfig.vModel] = acc[filterConfig.vModel] ?? '';
              break;
            case 'checkbox':
              result[filterConfig.vModel] = acc[filterConfig.vModel] ?? false;
              break;
            case 'multi-select':
              result[filterConfig.vModel] = acc[filterConfig.vModel] ?? [];
              break;
            case 'tree-select':
              result[filterConfig.vModel] = acc[filterConfig.vModel] ?? [];
              break;
            case 'date':
            case 'time':
            case 'rangeNumber':
              result[this.getRangeFromVModelKey(filterConfig)] = acc[this.getRangeFromVModelKey(filterConfig)] ?? '';
              result[this.getRangeToVModelKey(filterConfig)] = acc[this.getRangeToVModelKey(filterConfig)] ?? '';
              break;
            case 'product':
              result[`${filterConfig.vModel}${FilterPostfixKey.CODES}`] =
                acc[`${filterConfig.vModel}${FilterPostfixKey.CODES}`] ?? [];
              break;
          }
        });

        return result;
      },

      getVModelKey(filterConfig: TBaseFilter): string[] | string {
        switch (filterConfig.type) {
          case 'text':
          case 'select':
          case 'multi-select':
          case 'tree-select':
          case 'checkbox':
            return filterConfig.vModel;
          case 'date':
          case 'time':
          case 'rangeNumber': {
            const rangeFromKey = this.getRangeFromVModelKey(filterConfig);
            const rangeToKey = this.getRangeToVModelKey(filterConfig);
            return [rangeFromKey, rangeToKey];
          }
          case 'product':
            return `${filterConfig.vModel}${FilterPostfixKey.CODES}`;
        }
      },

      getRangeVModelKey(filterConfig: TBaseFilterRange, type: RangeTypes) {
        if (!filterConfig.range) return `${filterConfig.vModel}${type}`;

        const prefixKey = type.toLowerCase() as keyof NonNullable<TBaseFilterRange['range']>['prefix'];
        const postfixKey = type.toLowerCase() as keyof NonNullable<TBaseFilterRange['range']>['postfix'];
        const prefixValue = filterConfig?.range?.prefix?.[prefixKey] ?? '';
        const postfixValue = filterConfig?.range?.postfix?.[postfixKey] ?? '';
        const vModelValue = prefixValue ? this.capitalize(filterConfig.vModel) : filterConfig.vModel;

        return `${prefixValue}${vModelValue}${postfixValue}`;
      },

      getRangeFromVModelKey(filterConfig: TBaseFilterRange) {
        return this.getRangeVModelKey(filterConfig, RangeTypes.FROM);
      },

      getRangeToVModelKey(filterConfig: TBaseFilterRange) {
        return this.getRangeVModelKey(filterConfig, RangeTypes.TO);
      },

      initFilter() {
        this.filter = this.getDefaultFilter(this.filtersConfig, this.defaultFilter);
      },

      initFilterSnapshots() {
        this.filterFirstSnapshot = this.filterSnapshot = this.getFilter;
      },

      searchFilter() {
        this.v$.$touch();
        if (this.v$.$invalid) return;
        this.updateFilter();
      },

      resetFilter() {
        this.filter = this.getDefaultFilter(this.filtersConfig, {});

        this.$emit('resetFilter');

        if (this.getIsFilterSnapshotEmpty) return;

        this.updateFilter();
      },

      updateFilter() {
        this.$emit('searchFilter', this.getFilter);
        this.filterSnapshot = this.getFilter;
      },

      updateSelectedIDs(ids: number[], vModel: string) {
        this.filter[vModel] = ids;
      },

      getDatePickerMode(filterConfig: TBaseFilterDates) {
        return filterConfig.mode ?? 'date';
      },

      getSelectOptions(selectFilter: TBaseFilterSelect) {
        return JSON.parse(JSON.stringify(this.getFilteredSelectOptions(selectFilter)));
      },

      getFilteredSelectOptions(selectFilter: TBaseFilterSelect) {
        if (!selectFilter.filters) return selectFilter.options;

        const selectedSelectFilters =
          selectFilter.filters.filter((filter) => {
            if (Array.isArray(this.filter[filter.source])) {
              return !!(this.filter[filter.source] as Array<string>).length;
            }

            return this.filter[filter.source];
          }) ?? [];

        if (selectedSelectFilters.length === 0) return selectFilter.options;

        return selectFilter.options.filter((option) =>
          selectedSelectFilters.every((filter) => {
            const optionValue = (option as any)[filter.key];

            return (
              this.filter[filter.source] === optionValue ||
              (Array.isArray(this.filter[filter.source]) &&
                (this.filter[filter.source] as Array<string>).includes(String(optionValue)))
            );
          }),
        );
      },

      filterSelectFilters(selectFilter: TBaseFilter) {
        const filteredSelectFilters = this.filtersConfig.filter(
          (filterConfig): filterConfig is TFilterSelect | TFilterMultiSelect =>
            (filterConfig.type === 'select' || filterConfig.type === 'multi-select') &&
            (filterConfig.filters?.some((filter) => filter.source === selectFilter.vModel) ?? false),
        );

        if (!filteredSelectFilters.length) return;

        filteredSelectFilters.forEach((filterConfig) => {
          const selectFilterValue = this.filter[filterConfig.vModel];
          if (!selectFilterValue) return;

          const selectFilterOption = this.getFilteredSelectOptions(filterConfig).find(
            (option: (typeof filterConfig.options)[number]) => option[filterConfig.valueProp] === selectFilterValue,
          );
          if (selectFilterOption) return;

          this.filter[filterConfig.vModel] = Array.isArray(selectFilterValue) ? [] : '';
        });
      },

      resetFilterValue(filterConfig: TFilterConfig) {
        const defaultValue = this.getDefaultFilter([filterConfig], {});
        const key = this.getVModelKey(filterConfig);

        if (Array.isArray(key)) {
          key.forEach((keyItem) => {
            this.filter[keyItem] = defaultValue[keyItem] ?? '';
          });
        } else {
          this.filter[key] = defaultValue[key] ?? '';
        }
      },

      getTreeMultiselectFilterValue(filterConfig: TFilterTreeSelect): (string | number)[] | undefined {
        const value = this.filter[filterConfig.vModel];

        if (Array.isArray(value)) {
          return value;
        }

        return undefined;
      },

      setTreeMultiselectSelectValue(value: Array<number>, filterConfig: TFilterTreeSelect) {
        let result = value;

        if (filterConfig.isSingleMode) {
          const lastValue = value[value.length - 1];
          result = lastValue == undefined ? [] : [lastValue];
        }

        this.filter[filterConfig.vModel] = result;
      },

      getRangeValue(filterConfig: TBaseFilterRange, isFrom: boolean): string | number | undefined {
        const key = isFrom ? this.getRangeFromVModelKey(filterConfig) : this.getRangeToVModelKey(filterConfig);
        const value = this.filter[key];

        return typeof value === 'string' || typeof value === 'number' ? value : undefined;
      },

      setRangeValue(filterConfig: TBaseFilterRange, isFrom: boolean, value: string | number | undefined) {
        const key = isFrom ? this.getRangeFromVModelKey(filterConfig) : this.getRangeToVModelKey(filterConfig);
        this.filter[key] = value ?? '';
      },

      getProductSelectedIds(filterConfig: TFilterConfig): (string | number)[] {
        const key = `${filterConfig.vModel}${FilterPostfixKey.CODES}`;
        const value = this.filter[key];

        if (Array.isArray(value)) {
          return value;
        }

        return [];
      },

      handleDisabledFilterReset(filterConfig: TFilterConfig) {
        if (!filterConfig.isKeepValueOnDisable && this.getIsResettableDisabled(filterConfig)) {
          this.resetFilterValue(filterConfig);
        }
      },

      getFilterTitle(filterConfig: TFilterConfig) {
        // у чекбокса подпись это его собственный label
        if (filterConfig.type === 'checkbox') return '';
        if (filterConfig.type !== 'product') return filterConfig.label;
        return 'Товар';
      },

      getIsResettableDisabled(filterConfig: TFilterConfig) {
        return filterConfig.disabled || (filterConfig.disabledWhen?.(this.filter) ?? false);
      },

      getIsDisabledFilter(filterConfig: TFilterConfig) {
        return this.getIsResettableDisabled(filterConfig) || this.isReadonly;
      },
    },
    watch: {
      filter: {
        handler() {
          this.$emit('updateFilter', this.getFilter);
        },
        deep: true,
      },
      filtersConfig: {
        handler() {
          if (Object.entries(this.filter).some(([key]) => this.getFilter[key] !== this.filter[key])) {
            this.filter = this.getFilter;
          }
        },
        deep: true,
      },
      readonlyPermissionCode: {
        async handler(newValue: number) {
          if (newValue) {
            this.isReadonly = !(await this.checkPermission(this.readonlyPermissionCode)).data;
          } else {
            this.isReadonly = false;
          }
        },
        immediate: true,
      },
    },
  });
